import { createFileRoute, Link } from "@tanstack/react-router";
import heroStandard from "@/assets/hero-the-standard.jpg";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { DEFAULT_SITE_SETTINGS } from "@/lib/siteSettings";

export const Route = createFileRoute("/the-standard")({
  head: () => ({
    meta: [
      { title: "The Standard | Coyote Creek Hat Co." },
      { name: "description", content: "The Coyote Creek Standard — uncompromising craftsmanship, premium materials, and quality you can trust in the field." },
      { property: "og:title", content: "The Standard | Coyote Creek Hat Co." },
      { property: "og:description", content: "The Coyote Creek Standard — uncompromising craftsmanship and premium materials." },
      { property: "og:url", content: "https://coyotecreekhatco.com/the-standard" },
      { property: "og:image", content: `https://coyotecreekhatco.com${heroStandard}` },
      { name: "twitter:image", content: `https://coyotecreekhatco.com${heroStandard}` },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/the-standard" }],
  }),
  component: StandardPage,
});

const PILLARS = [
  { n: "01", title: "Premium Materials", desc: "Heavyweight twill, structured buckram, and durable mesh — no shortcuts." },
  { n: "02", title: "Precision Embroidery", desc: "Tight, clean stitching that holds up to sweat, mud, and time." },
  { n: "03", title: "Built to Fit", desc: "Adjustable closures and crown profiles tested in the field." },
  { n: "04", title: "Tailored to your lifestyle", desc: "Our hats are meant to be worn for something you stand for." },
];

const MARQUEE_ITEMS = [
  "Best Dadgum Standard",
  "No Shortcuts",
  "Field Tested",
  "Built To Last",
  "Texas Rooted",
];

function StandardPage() {
  const { data: settings } = useSiteSettings();
  const marqueeBg = settings?.feature_marquee_bg_color ?? DEFAULT_SITE_SETTINGS.feature_marquee_bg_color;
  const marqueeText = settings?.feature_marquee_text_color ?? DEFAULT_SITE_SETTINGS.feature_marquee_text_color;
  return (
    <div className="bg-background">
      {/* Hero */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img
          src={heroStandard}
          alt="The Standard"
          className="absolute inset-0 h-full w-full object-cover"
          width={1920}
          height={768}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white text-center px-6 drop-shadow-lg">
            The Standard
          </h1>
        </div>
        {/* Scroll cue */}
        <div className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-3 md:flex">
          <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-white/90 [writing-mode:vertical-rl]">
            Scroll
          </span>
          <span className="block h-12 w-px bg-white/40" />
        </div>
      </div>

      {/* 2. MANIFESTO INTRO */}
      <section className="bg-[#E8DCC4] py-24 md:py-36">
        <div className="mx-auto grid max-w-[1300px] grid-cols-1 gap-12 px-6 lg:grid-cols-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
                01 / The Standard
              </span>
              <div className="mt-6 h-px w-16 bg-primary/30" />
              <p className="mt-6 font-ui text-xs uppercase tracking-[0.25em] text-primary/70">
                Our Standard
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <h2 className="font-heading text-3xl font-black uppercase tracking-tight text-primary md:text-5xl">
              The Coyote Creek Standard
            </h2>
            <p className="mt-8 font-heading text-2xl leading-[1.2] tracking-tight text-primary sm:text-3xl md:text-4xl uppercase">
              <span
                className="float-left mr-3 font-heading text-7xl font-black leading-[0.85] md:text-8xl"
                style={{ color: "#fa5021" }}
              >
                E
              </span>
              very hat we make is held to one standard — the best dadgum standard. From the materials we source to the stitch on the brim.
            </p>
          </div>
        </div>
      </section>

      {/* 3. MARQUEE DIVIDER */}
      <section className="overflow-hidden py-5" style={{ backgroundColor: marqueeBg, color: marqueeText }}>
        <div className="flex w-max animate-marquee whitespace-nowrap will-change-transform">
          {[0, 1, 2].map((g) => (
            <div key={g} className="flex shrink-0 items-center gap-8 px-4">
              {MARQUEE_ITEMS.map((item) => (
                <span key={item} className="flex shrink-0 items-center gap-8">
                  <span className="font-ui text-xs font-bold uppercase tracking-[0.25em]">
                    {item}
                  </span>
                  <span className="text-sm">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* 4. HOW WE BUILD — Numbered Pillars */}
      <section className="bg-card py-24 text-primary md:py-36">
        <div className="mx-auto grid max-w-[1300px] grid-cols-1 gap-12 px-6 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
                02 / How We Build
              </span>
              <h2 className="mt-6 font-heading text-5xl font-black uppercase leading-[0.95] tracking-tight md:text-6xl">
                How We Build
              </h2>
              <div
                className="mt-8 h-1 w-16"
                style={{ backgroundColor: "#fa5021" }}
              />
              <p className="mt-8 max-w-sm font-body text-sm leading-relaxed text-primary/70 md:text-base">
                Four non-negotiables behind every hat we send out the door.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="border-t border-primary/15">
              {PILLARS.map((p) => (
                <li
                  key={p.n}
                  className="grid grid-cols-[auto_1fr] gap-6 border-b border-primary/15 py-8 md:gap-10 md:py-10"
                >
                  <span
                    className="font-heading text-5xl font-black leading-none md:text-6xl"
                    style={{ color: "#fa5021" }}
                  >
                    {p.n}
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-black uppercase tracking-tight text-primary md:text-2xl">
                      {p.title}
                    </h3>
                    <p className="mt-3 font-body text-sm leading-relaxed text-primary/70 md:text-base">
                      {p.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 5. PROMISE CLOSE */}
      <section className="bg-secondary py-32 text-primary md:py-44">
        <div className="mx-auto max-w-[1100px] px-6 text-center">
          <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/80">
            03 / Our Promise
          </span>
          <h2 className="sr-only">Our Promise</h2>
          <p className="mx-auto mt-10 max-w-4xl font-heading text-3xl italic leading-[1.1] tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
            “Just like we take pride in our heritage, we take pride in our quality.”
          </p>

          <div className="mx-auto mt-12 h-px w-20 bg-primary/40" />
          <p className="mt-6 font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/80">
            — Coyote Creek Hat Co.
          </p>

          <Link
            to="/heritage"
            className="mt-12 btn-cc-primary tracking-[0.2em]"
          >
            Explore Our Heritage
          </Link>
        </div>
      </section>
    </div>
  );
}
